<script setup lang="ts">
import { ref, watch } from 'vue';
import BaseModal from '@/components/BaseModal.vue';
import { api, ApiError } from '@/composables/useApi';
import { Eye, EyeOff } from 'lucide-vue-next';

/**
 * Self-service password change. Asks for the current password (to defend
 * against a stolen session silently rotating credentials), the new one,
 * and a confirmation. Posts to /api/me/password.
 */

const props = defineProps<{ open: boolean }>();
const emit  = defineEmits<{ (e: 'close'): void }>();

const current = ref('');
const next    = ref('');
const confirm = ref('');
const showCur = ref(false);
const showNew = ref(false);
const error   = ref<string | null>(null);
const success = ref(false);
const submitting = ref(false);

// Reset every time the modal opens so a previously-typed (and possibly
// wrong) value doesn't haunt the next attempt.
watch(() => props.open, (open) => {
    if (open) {
        current.value = ''; next.value = ''; confirm.value = '';
        showCur.value = false; showNew.value = false;
        error.value = null; success.value = false;
    }
});

async function onSubmit() {
    error.value = null;
    if (!current.value || !next.value || !confirm.value) {
        error.value = 'Fill in all three fields.';
        return;
    }
    if (next.value.length < 8) {
        error.value = 'New password must be at least 8 characters.';
        return;
    }
    if (next.value !== confirm.value) {
        error.value = "The two new passwords don't match.";
        return;
    }
    if (next.value === current.value) {
        error.value = 'New password must be different from the current one.';
        return;
    }
    submitting.value = true;
    try {
        await api('/me/password', {
            method: 'POST',
            body: { currentPassword: current.value, newPassword: next.value },
        });
        success.value = true;
        // Give the user a beat to see the success line, then close.
        setTimeout(() => emit('close'), 1400);
    } catch (e) {
        if (e instanceof ApiError) {
            if (e.status === 401)      error.value = 'Current password is incorrect.';
            else if (e.status === 400) error.value = e.message || 'Invalid input.';
            else                       error.value = 'Could not change password (' + e.status + ').';
        } else {
            error.value = 'Could not reach the server.';
        }
    } finally {
        submitting.value = false;
    }
}
</script>

<template>
    <BaseModal :open="open" title="Change password" width-class="w-[min(94vw,420px)]" @close="emit('close')">
        <form v-if="!success" @submit.prevent="onSubmit" class="space-y-3">
            <p class="text-[12px] text-frvb-muted -mt-1">
                Pick a password only you know — at least 8 characters.
            </p>

            <label class="block">
                <span class="cp-label">Current password</span>
                <div class="cp-row">
                    <input v-model="current" :type="showCur ? 'text' : 'password'"
                           autocomplete="current-password" class="cp-input" />
                    <button type="button" @click="showCur = !showCur" class="cp-action"
                            :aria-label="showCur ? 'Hide' : 'Show'">
                        <Eye v-if="!showCur" :size="15" /><EyeOff v-else :size="15" />
                    </button>
                </div>
            </label>

            <label class="block">
                <span class="cp-label">New password</span>
                <div class="cp-row">
                    <input v-model="next" :type="showNew ? 'text' : 'password'"
                           autocomplete="new-password" class="cp-input" />
                    <button type="button" @click="showNew = !showNew" class="cp-action"
                            :aria-label="showNew ? 'Hide' : 'Show'">
                        <Eye v-if="!showNew" :size="15" /><EyeOff v-else :size="15" />
                    </button>
                </div>
            </label>

            <label class="block">
                <span class="cp-label">Confirm new password</span>
                <input v-model="confirm" :type="showNew ? 'text' : 'password'"
                       autocomplete="new-password" class="cp-input" />
            </label>

            <p v-if="error" class="cp-error">{{ error }}</p>
        </form>

        <p v-else class="cp-success">
            Password changed. You can use the new one next time you sign in.
        </p>

        <template v-if="!success" #footer>
            <button @click="emit('close')" type="button"
                    class="px-3 py-1.5 rounded-md bg-white border border-frvb-border-s text-frvb-ink text-sm font-medium hover:bg-frvb-bg-alt">
                Cancel
            </button>
            <button @click="onSubmit" :disabled="submitting" type="button"
                    class="px-4 py-1.5 rounded-md bg-frvb-accent hover:bg-frvb-accent-2 text-white text-sm font-semibold disabled:opacity-60">
                {{ submitting ? 'Saving…' : 'Update password' }}
            </button>
        </template>
    </BaseModal>
</template>

<style scoped>
.cp-label {
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--color-frvb-ink-soft, #475569);
    margin-bottom: 0.25rem;
}
.cp-row { position: relative; }
.cp-input {
    width: 100%;
    padding: 0.55rem 2.5rem 0.55rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--color-frvb-border-s, #cbd5e1);
    background: #ffffff;
    color: var(--color-frvb-ink, #0f172a);
    font-size: 0.9rem;
    outline: none;
}
.cp-input:focus {
    border-color: var(--color-frvb-accent, #0d9488);
    box-shadow: 0 0 0 3px rgba(45, 212, 191, 0.15);
}
.cp-action {
    position: absolute;
    right: 0.35rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.8rem;
    height: 1.8rem;
    border: 0;
    background: transparent;
    color: var(--color-frvb-muted, #64748b);
    border-radius: 0.4rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.cp-action:hover { background: rgba(15, 23, 42, 0.06); color: var(--color-frvb-ink, #0f172a); }
.cp-error {
    color: #b91c1c;
    background: #fee2e2;
    border: 1px solid #fca5a5;
    padding: 0.5rem 0.7rem;
    border-radius: 0.4rem;
    font-size: 0.78rem;
    margin: 0;
}
.cp-success {
    text-align: center;
    color: #065f46;
    background: #d1fae5;
    border: 1px solid #6ee7b7;
    padding: 0.85rem 0.7rem;
    border-radius: 0.5rem;
    font-size: 0.88rem;
    font-weight: 600;
}
</style>
